/*
 * Соединение с сервером.
 *
 * Одно на всё приложение. Само переподключается при обрыве и после
 * восстановления досылает запрос на догон: пока связи не было, в чатах
 * могли появиться сообщения, и молча их пропустить нельзя.
 */

import {
	envelope,
	type ClientMessage,
	type ProtocolErrorCode,
	type ServerMessage
} from '@andresot/protocol/envelope';
import { getAccessToken, restoreSession, type RefreshResult } from './client';
import { appOnScreen, watchOnScreen } from '$utils/on-screen';

/*
 * Код закрытия на время технических работ. Свой, не 4001: тот означает
 * «сессия кончилась», и приложение пошло бы выкидывать человека из аккаунта,
 * хотя с доступом всё в порядке — закрыт сам мессенджер.
 */
const MAINTENANCE_CLOSE_CODE = 4503;

type Handler = (message: ServerMessage) => void;

export type SocketPhase = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'unauthorized';

export class SocketRequestError extends Error {
	constructor(
		message: string,
		readonly code: ProtocolErrorCode
	) {
		super(message);
		this.name = 'SocketRequestError';
	}
}

/** Задержки между попытками — растут, чтобы не долбить упавший сервер. */
const BACKOFF_MS = [500, 1000, 2000, 5000, 10_000, 20_000];

/*
 * Как часто повторять отметку «приложение на экране».
 *
 * Вдвое чаще, чем срок отметки на сервере: одна потерянная посылка ничего не
 * меняет, а лишний кадр раз в полминуты не стоит ничего.
 */
const VISIBILITY_REFRESH_MS = 30_000;

class Socket {
	private ws: WebSocket | null = null;
	private attempt = 0;
	private closedByUs = false;
	private timer: ReturnType<typeof setTimeout> | null = null;
	/** Инвалидирует обработчики прежнего соединения при stop/restart. */
	private generation = 0;
	private authRecovery: Promise<RefreshResult> | null = null;

	private handlers = new Set<Handler>();
	private phaseHandlers = new Set<(phase: SocketPhase) => void>();

	/** Подтверждения, которых ждём: идентификатор конверта → обещание. */
	private pending = new Map<
		string,
		{ resolve: (message: ServerMessage) => void; reject: (error: Error) => void }
	>();

	phase: SocketPhase = 'idle';

	constructor() {
		/*
		 * Возвращение сети — повод пересобрать связь, не дожидаясь молчания.
		 *
		 * Оборвавшийся канал закрывается не сразу: когда сеть пропадает
		 * (метро, лифт, включили VPN), браузер узнаёт об этом только по
		 * тишине, а это минуты. Всё это время приложение уверено, что
		 * связано, — и ничего не досылает.
		 */
		if (typeof window !== 'undefined') {
			window.addEventListener('online', () => this.wakeUp());
		}

		/*
		 * Пока приложение на экране, сервер об этом знает и не шлёт системных
		 * уведомлений: сообщение и так приходит по этому соединению, а баннер
		 * поверх открытого приложения только мешает.
		 *
		 * Отметку повторяем: у неё на сервере есть срок, и одна потерянная
		 * посылка не должна оставлять человека без уведомлений навсегда.
		 *
		 * «На экране» решает appOnScreen: в приложении для Windows страница видна
		 * всегда, и по одной видимости сервер молчал бы, пока оно запущено.
		 */
		if (typeof document !== 'undefined') {
			watchOnScreen(() => this.reportVisibility());
			// Повтор живёт столько же, сколько страница: гасить его незачем и негде.
			setInterval(() => this.reportVisibility(), VISIBILITY_REFRESH_MS);
		}
	}

	/** Сообщить серверу, видно ли приложение сейчас. Молча: это отметка. */
	private reportVisibility(): void {
		if (typeof document === 'undefined' || !this.isOpen) return;
		this.send(envelope('app.state', { visible: appOnScreen() }));
	}

	connect(): void {
		this.closedByUs = false;
		if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
			return;
		}

		this.attempt = 0;
		this.open();
	}

	private open(): void {
		const token = getAccessToken();
		if (!token || this.closedByUs) {
			this.setPhase(token ? 'idle' : 'unauthorized');
			return;
		}

		const generation = ++this.generation;

		// Прежний канал закрываем сами: смена поколения только отключает его
		// обработчики, а сам он остался бы висеть — и у нас, и на сервере.
		this.ws?.close();
		this.ws = null;
		// Ответы на отправленное в прежний канал уже не придут.
		this.failPending('Соединение пересобрано');

		this.setPhase(this.attempt === 0 ? 'connecting' : 'reconnecting');

		// Соединение идёт на тот же узел, что и страница: в разработке его
		// переправляет Vite, в бою — nginx. Схема выбирается по схеме страницы,
		// иначе на https браузер откажется открывать незащищённое соединение.
		const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
		const ws = new WebSocket(`${scheme}://${location.host}/ws`);

		this.ws = ws;

		ws.onopen = () => {
			if (generation !== this.generation) return;
			ws.send(JSON.stringify({ op: 'auth', token }));
		};

		ws.onmessage = (event: MessageEvent<string>) => {
			if (generation !== this.generation) return;
			let message: ServerMessage;

			try {
				message = JSON.parse(event.data) as ServerMessage;
			} catch {
				return;
			}
			if (message.op === 'auth.ready') {
				this.attempt = 0;
				this.setPhase('open');
				this.reportVisibility();
				return;
			}

			// Подтверждения и отказы уходят тому, кто их ждёт; остальное —
			// всем подписчикам.
			const waiting = this.pending.get(message.id);

			if (waiting && (message.op === 'ack' || message.op === 'error')) {
				this.pending.delete(message.id);

				if (message.op === 'error') {
					waiting.reject(new SocketRequestError(message.payload.message, message.payload.code));
				} else {
					waiting.resolve(message);
				}

				return;
			}

			for (const handler of this.handlers) handler(message);
		};

		ws.onclose = (event) => {
			if (generation !== this.generation) return;
			this.ws = null;
			/*
			 * Ждать ответа больше не от кого. Раньше отправленное досиживало свои
			 * десять секунд — и по их истечении пересобирало уже новое, здоровое
			 * соединение.
			 */
			this.failPending('Соединение закрыто');

			if (this.closedByUs) {
				this.setPhase('idle');
				return;
			}

			if (event.code === 4001) {
				void this.recoverAuthentication(generation);
				return;
			}

			/*
			 * Технические работы. Переподключаться незачем: сервер закроет
			 * соединение снова, и попытки шли бы по кругу до конца работ.
			 * Экран работ спрашивает сервер сам и перезапустит приложение,
			 * когда работы кончатся.
			 */
			if (event.code === MAINTENANCE_CLOSE_CODE) {
				this.setPhase('idle');
				return;
			}

			this.scheduleReconnect();
		};

		// Ошибку не обрабатываем отдельно: за ней всегда следует закрытие,
		// и переподключение запустится там.
		ws.onerror = () => undefined;
	}

	/** Обновить короткий токен и только потом повторить handshake. */
	private async recoverAuthentication(generation: number): Promise<void> {
		this.setPhase('reconnecting');
		this.authRecovery ??= restoreSession();

		try {
			const restored = await this.authRecovery;
			if (generation !== this.generation || this.closedByUs) return;

			/*
			 * Сетевой сбой — не отказ в доступе. Прежде здесь было «не вышло —
			 * значит выгоняем»: короткий обрыв во время обновления токена
			 * приводил к выходу из аккаунта, хотя сессия оставалась живой.
			 */
			if (restored === 'offline') {
				this.scheduleReconnect();
				return;
			}

			if (restored === 'expired') {
				this.setPhase('unauthorized');
				return;
			}

			this.attempt = 0;
			this.open();
		} finally {
			this.authRecovery = null;
		}
	}

	/**
	 * Сеть вернулась — подключаемся сейчас, а не по расписанию.
	 *
	 * Задержки между попытками растут до двадцати секунд; ждать их, когда сеть
	 * уже есть, незачем.
	 */
	private wakeUp(): void {
		if (this.closedByUs) return;

		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
			this.attempt = 0;
			this.open();
			return;
		}

		this.revive();
	}

	/**
	 * Пересобрать соединение, которое считается живым, но им не является.
	 *
	 * Мёртвый канал закрывает и отключает сам `open`; здесь остаётся решить,
	 * что пересобирать есть что: если сокета нет, попытка уже назначена.
	 */
	private revive(): void {
		if (this.closedByUs || !this.ws) return;

		this.attempt = 0;
		this.open();
	}

	private scheduleReconnect(): void {
		if (this.closedByUs) return;
		this.setPhase('reconnecting');

		const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]!;
		this.attempt += 1;

		this.timer = setTimeout(() => {
			this.timer = null;
			this.open();
		}, delay);
	}

	disconnect(): void {
		this.closedByUs = true;
		this.generation += 1;

		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
		this.ws?.close();
		this.ws = null;

		// Ожидающие подтверждения отпускаем с отказом: иначе обещания
		// повисли бы навсегда, и отправка «застряла бы» без объяснения.
		this.failPending('Соединение закрыто');
		this.setPhase('idle');
	}

	/**
	 * Отпустить всех, кто ждёт подтверждения: канала, по которому оно пришло бы,
	 * больше нет. Это обрыв, а не отказ сервера — отправка останется в очереди.
	 */
	private failPending(reason: string): void {
		const waiting = [...this.pending.values()];
		this.pending.clear();
		for (const entry of waiting) entry.reject(new Error(reason));
	}

	get isOpen(): boolean {
		return this.ws?.readyState === WebSocket.OPEN;
	}

	/** Отправить без ожидания ответа — для «печатает». */
	send(message: ClientMessage): void {
		if (!this.isOpen) return;
		this.ws!.send(JSON.stringify(message));
	}

	/**
	 * Отправить и дождаться подтверждения. Идентификатор можно задать
	 * снаружи — он же служит ключом от повторов: та же отправка после
	 * обрыва связи не создаст на сервере второе сообщение.
	 */
	request<Op extends ClientMessage['op']>(
		op: Op,
		payload: Extract<ClientMessage, { op: Op }>['payload'],
		id?: string
	): Promise<ServerMessage> {
		return new Promise((resolve, reject) => {
			if (!this.isOpen) {
				reject(new Error('Нет соединения'));
				return;
			}

			const message = envelope(op, payload, id);
			const entry = { resolve, reject };
			// Повтор с тем же ключом: прежняя попытка ответа уже не дождётся.
			this.pending.get(message.id)?.reject(new Error('Запрос повторён'));
			this.pending.set(message.id, entry);

			// Ответ, который не пришёл за десять секунд, считаем потерянным.
			// Без этого запись в списке ожидания осталась бы навсегда.
			setTimeout(() => {
				// Только свою запись: под тем же ключом уже может ждать повтор, и
				// снять его значило бы потерять подтверждение дошедшего сообщения.
				if (this.pending.get(message.id) !== entry) return;
				this.pending.delete(message.id);
				reject(new Error('Сервер не ответил'));

				// Молчание в ответ — почти всегда мёртвый канал. Пересобираем
				// его сами: иначе приложение так и осталось бы «на связи»,
				// а неотправленное — лежать до перезагрузки страницы.
				this.revive();
			}, 10_000);

			this.ws!.send(JSON.stringify(message));
		});
	}

	onMessage(handler: Handler): () => void {
		this.handlers.add(handler);
		return () => this.handlers.delete(handler);
	}

	onPhase(handler: (phase: SocketPhase) => void): () => void {
		this.phaseHandlers.add(handler);
		return () => this.phaseHandlers.delete(handler);
	}

	private setPhase(phase: SocketPhase): void {
		if (this.phase === phase) return;

		this.phase = phase;
		for (const handler of this.phaseHandlers) handler(phase);
	}
}

export const socket = new Socket();
